import React, { useState, useEffect, useCallback } from "react";
import { Plus, Trash, HandCoins, PencilSimple, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { fmtBRL, fmtDate, todayISO, PAYMENT_METHODS } from "../lib/format";
import { StatusBadge } from "./StatusBadge";
import { DatePicker } from "./DatePicker";
import { PaymentResultDialog } from "./PaymentResultDialog";
import { ReceiptMenu } from "./ReceiptMenu";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const input = "bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";
const emptyPay = () => ({ payment_date: todayISO(), amount: "", payment_method: "PIX", admin_notes: "" });

export const OrderPaymentsDialog = ({ order, onClose, onChanged }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [form, setForm] = useState(emptyPay());
  const [excessAlloc, setExcessAlloc] = useState({});
  const [saving, setSaving] = useState(false);
  const [creditAmount, setCreditAmount] = useState("");
  const [payResult, setPayResult] = useState(null);
  const [editNote, setEditNote] = useState(null);

  const load = useCallback(async () => {
    if (!order) return;
    try {
      const { data: d } = await api.get(`/orders/${order.id}/payments`);
      setData(d);
    } catch (err) {
      toast.error(apiError(err));
    }
  }, [order]);

  useEffect(() => {
    setData(null);
    setForm(emptyPay());
    setExcessAlloc({});
    load();
  }, [load]);

  const amountNum = parseFloat(form.amount) || 0;
  const excess = data && data.balance > 0 ? Math.round(Math.max(amountNum - data.balance, 0) * 100) / 100 : 0;
  const otherOrders = data?.other_open_orders || [];
  const excessAllocTotal = Math.round(otherOrders.reduce((s, o) => s + (parseFloat(excessAlloc[o.order_id]) || 0), 0) * 100) / 100;
  const excessLeft = Math.round((excess - excessAllocTotal) * 100) / 100;
  const excessInvalid = excess > 0.009 && (excessLeft < -0.009 ||
    otherOrders.some((o) => (parseFloat(excessAlloc[o.order_id]) || 0) > o.balance + 0.009));

  useEffect(() => {
    if (excess <= 0.009 && Object.keys(excessAlloc).length) setExcessAlloc({});
  }, [excess, excessAlloc]);

  const addPayment = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (excessInvalid) {
      toast.error("Ajuste a distribuição da sobra antes de salvar");
      return;
    }
    setSaving(true);
    try {
      const excess_allocations = excess > 0.009
        ? Object.entries(excessAlloc)
            .map(([order_id, v]) => ({ order_id, amount: parseFloat(v) || 0 }))
            .filter((a) => a.amount > 0)
        : [];
      const { data: r } = await api.post(`/orders/${order.id}/payments`, {
        ...form, amount: parseFloat(form.amount), admin_notes: isAdmin ? form.admin_notes : undefined,
        excess_allocations,
      });
      if (r.payment_summary) {
        setPayResult(r.payment_summary);
      } else if (r.credit_generated > 0) {
        toast.success(`Pagamento registrado — ${fmtBRL(r.credit_generated)} viraram crédito do cliente para o próximo pedido (crédito total: ${fmtBRL(r.client_credit)})`);
      } else {
        toast.success(r.balance <= 0 ? "Pagamento registrado — pedido Liquidado!" : `Pagamento registrado — saldo devedor ${fmtBRL(r.balance)}`);
      }
      setForm(emptyPay());
      setExcessAlloc({});
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(apiError(err));
    }
    setSaving(false);
  };

  const applyCredit = async (e) => {
    e.preventDefault();
    try {
      const { data: r } = await api.post(`/orders/${order.id}/apply-credit`, {
        amount: creditAmount ? parseFloat(creditAmount) : null,
      });
      if (r.payment_summary) {
        setPayResult(r.payment_summary);
      } else {
        toast.success(r.balance <= 0
          ? `${fmtBRL(r.applied)} de crédito abatido — pedido Liquidado! Crédito restante: ${fmtBRL(r.client_credit)}`
          : `${fmtBRL(r.applied)} de crédito abatido — saldo devedor ${fmtBRL(r.balance)}. Crédito restante: ${fmtBRL(r.client_credit)}`);
      }
      setCreditAmount("");
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const removePayment = async (paymentId) => {
    if (!window.confirm("Excluir este pagamento?")) return;
    try {
      const { data } = await api.delete(`/orders/${order.id}/payments/${paymentId}`);
      if (data.credit_removed > 0) {
        toast.success(`Pagamento excluído — crédito de ${fmtBRL(data.credit_removed)} gerado por ele também foi removido`);
      } else {
        toast.success("Pagamento excluído");
      }
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const saveNote = async () => {
    try {
      await api.patch(`/orders/${order.id}/payments/${editNote.id}/notes`, { admin_notes: editNote.text });
      toast.success("Detalhe adicional salvo");
      setEditNote(null);
      await load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <>
      <Dialog open={!!order} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">
            Histórico de Pagamentos — Pedido #{order?.code} · {order?.client_name}
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Registre recebimentos parciais até quitar o saldo do pedido
          </DialogDescription>
        </DialogHeader>
        {data && (
          <div className="space-y-5" data-testid="payments-dialog">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="border border-zinc-800 rounded p-3">
                <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">Valor Total</p>
                <p className="font-display font-semibold">{fmtBRL(data.total_value)}</p>
              </div>
              <div className="border border-zinc-800 rounded p-3">
                <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">Total Pago</p>
                <p data-testid="payments-paid-total" className="font-display font-semibold text-emerald-400">{fmtBRL(data.paid_total)}</p>
              </div>
              <div className="border border-zinc-800 rounded p-3">
                <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">Saldo Devedor</p>
                <p data-testid="payments-balance" className={`font-display font-semibold ${data.balance > 0 ? "text-rose-400" : "text-emerald-400"}`}>{fmtBRL(data.balance)}</p>
              </div>
              <div className="border border-zinc-800 rounded p-3 flex flex-col justify-between">
                <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">Status</p>
                <StatusBadge status={data.payment_status} testId="payments-status" />
              </div>
            </div>

            {data.balance > 0 && data.client_credit > 0 && (
              <form onSubmit={applyCredit} data-testid="apply-credit-section" className="border border-emerald-400/20 bg-emerald-400/5 rounded p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <HandCoins size={16} className="text-emerald-400" />
                  <p className="text-xs uppercase tracking-[0.15em] text-emerald-400">
                    Crédito disponível: <span data-testid="available-credit" className="font-semibold">{fmtBRL(data.client_credit)}</span>
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <label className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 block mb-1">Valor a Abater (R$)</label>
                    <input data-testid="apply-credit-amount-input" type="number" step="0.01" min="0.01"
                      max={Math.min(data.client_credit, data.balance)} className={`${input} w-full`}
                      placeholder={`Máx: ${fmtBRL(Math.min(data.client_credit, data.balance))}`}
                      value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)} />
                  </div>
                  <button data-testid="apply-credit-button"
                    className="sm:self-end flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium px-4 py-2.5 rounded transition-colors whitespace-nowrap">
                    <HandCoins size={15} /> Abater Crédito
                  </button>
                </div>
                <p className="text-[11px] text-zinc-500">Deixe o valor em branco para abater o máximo possível ({fmtBRL(Math.min(data.client_credit, data.balance))}).</p>
              </form>
            )}

            {data.balance > 0 ? (
              <form onSubmit={addPayment} className="border border-zinc-800 rounded p-4 space-y-3">
                <p className="text-xs uppercase tracking-[0.15em] text-zinc-500">Registrar Pagamento</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 block mb-1">Data do Recebimento</label>
                    <DatePicker testId="payment-date-input" value={form.payment_date}
                      onChange={(v) => setForm({ ...form, payment_date: v })} />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 block mb-1">Valor Pago (R$)</label>
                    <input data-testid="payment-amount-input" type="number" step="0.01" min="0.01" className={`${input} w-full`}
                      value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 block mb-1">Meio de Pagamento</label>
                    <select data-testid="payment-method-select" className={`${input} w-full`} value={form.payment_method}
                      onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
                      {PAYMENT_METHODS.map((p) => <option key={p}>{p}</option>)}
                    </select>
                  </div>
                </div>
                {isAdmin && (
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 block mb-1">
                      Detalhes Adicionais <span className="text-amber-500/80">(visível apenas para administradores)</span>
                    </label>
                    <textarea data-testid="payment-admin-notes-input" rows={2} className={`${input} w-full resize-none`}
                      placeholder="Ex: banco, nº do cheque, conta de destino..."
                      value={form.admin_notes} onChange={(e) => setForm({ ...form, admin_notes: e.target.value })} />
                  </div>
                )}
                {excess > 0.009 && (
                  <div data-testid="excess-destination-section" className="border border-amber-400/25 bg-amber-400/5 rounded p-3 space-y-2">
                    <p className="text-xs uppercase tracking-[0.15em] text-amber-400">
                      Sobra de {fmtBRL(excess)} — escolha para onde ela vai
                    </p>
                    {otherOrders.length === 0 ? (
                      <p className="text-[11px] text-zinc-400">Não há outros pedidos em aberto — a sobra vira crédito do cliente automaticamente.</p>
                    ) : (
                      <>
                        <div className="space-y-1.5">
                          {otherOrders.map((o) => {
                            const v = parseFloat(excessAlloc[o.order_id]) || 0;
                            const over = v > o.balance + 0.009;
                            return (
                              <div key={o.order_id} className="flex items-center justify-between gap-3 text-sm">
                                <span className="text-zinc-300">
                                  Pedido #{o.code}
                                  <span className="text-zinc-500 text-xs ml-2">{fmtDate(o.order_date)} · saldo {fmtBRL(o.balance)}</span>
                                </span>
                                <input data-testid={`excess-alloc-${o.code}`}
                                  className={`w-28 bg-zinc-900 border rounded px-2 py-1 text-sm text-right focus:outline-none focus:ring-1 ${over ? "border-rose-500 ring-rose-500" : "border-zinc-700 focus:ring-amber-500"}`}
                                  inputMode="decimal" placeholder="0,00" value={excessAlloc[o.order_id] ?? ""}
                                  onChange={(e) => setExcessAlloc({ ...excessAlloc, [o.order_id]: e.target.value.replace(",", ".").replace(/[^0-9.]/g, "") })} />
                              </div>
                            );
                          })}
                        </div>
                        <p data-testid="excess-leftover-note" className={`text-[11px] ${excessLeft < -0.009 ? "text-rose-400" : "text-zinc-400"}`}>
                          {excessLeft < -0.009
                            ? `A distribuição excede a sobra em ${fmtBRL(Math.abs(excessLeft))}`
                            : `Restante que vira crédito do cliente: ${fmtBRL(Math.max(excessLeft, 0))}`}
                        </p>
                      </>
                    )}
                  </div>
                )}
                <button data-testid="save-payment-button" disabled={excessInvalid || saving}
                  className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-zinc-700 disabled:text-zinc-400 disabled:cursor-not-allowed text-white text-sm font-medium py-2.5 rounded transition-colors">
                  <Plus size={15} weight="bold" /> {saving ? "Registrando..." : "Registrar Pagamento"}
                </button>
              </form>
            ) : (
              <p data-testid="payments-settled-note" className="text-sm text-emerald-400 border border-emerald-400/20 bg-emerald-400/5 rounded px-3 py-2">
                Pedido liquidado — todos os pagamentos foram recebidos.
              </p>
            )}

            <div className="border border-zinc-800 rounded overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.1em] text-zinc-500 border-b border-zinc-800">
                    <th className="px-4 py-2.5 font-medium">Data</th>
                    <th className="px-4 py-2.5 font-medium">Valor</th>
                    <th className="px-4 py-2.5 font-medium">Meio de Pagamento</th>
                    <th className="px-4 py-2.5 font-medium text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.payments.length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-6 text-center text-zinc-500">Nenhum pagamento registrado ainda.</td></tr>
                  )}
                  {data.payments.map((p) => (
                    <React.Fragment key={p.id}>
                    <tr data-testid={`payment-row-${p.code}`} className="border-b border-zinc-800/60 hover:bg-zinc-800/40 transition-colors">
                      <td className="px-4 py-2.5 text-zinc-300">{fmtDate(p.payment_date)}</td>
                      <td className="px-4 py-2.5 font-medium text-emerald-400">{fmtBRL(p.amount)}</td>
                      <td className="px-4 py-2.5 text-zinc-400">{p.payment_method}</td>
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        <ReceiptMenu paymentId={p.id} testId={`receipt-payment-${p.code}`}
                          filename={`comprovante_${order?.code || "pagamento"}_${p.code || ""}.pdf`} />
                        {isAdmin && (
                          <button data-testid={`edit-notes-${p.code}`} title="Detalhes adicionais (admin)"
                            onClick={() => setEditNote({ id: p.id, text: p.admin_notes || "" })}
                            className="text-zinc-500 hover:text-amber-400 p-1 transition-colors"><PencilSimple size={14} /></button>
                        )}
                        <button data-testid={`delete-payment-${p.code}`} onClick={() => removePayment(p.id)}
                          className="text-zinc-500 hover:text-rose-400 p-1 transition-colors"><Trash size={14} /></button>
                      </td>
                    </tr>
                    {isAdmin && editNote?.id === p.id && (
                      <tr className="border-b border-zinc-800/60 bg-zinc-800/30">
                        <td colSpan={4} className="px-4 py-2.5">
                          <div className="flex items-start gap-2">
                            <textarea data-testid={`notes-edit-input-${p.code}`} rows={2} autoFocus
                              className={`${input} flex-1 resize-none`}
                              placeholder="Ex: banco, nº do cheque, conta de destino..."
                              value={editNote.text} onChange={(e) => setEditNote({ ...editNote, text: e.target.value })} />
                            <button data-testid={`notes-save-${p.code}`} onClick={saveNote}
                              className="bg-emerald-600 hover:bg-emerald-500 text-white p-2 rounded transition-colors"><Check size={14} /></button>
                            <button onClick={() => setEditNote(null)}
                              className="bg-zinc-800 hover:bg-zinc-700 text-zinc-300 p-2 rounded transition-colors"><X size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    )}
                    {isAdmin && editNote?.id !== p.id && p.admin_notes && (
                      <tr className="border-b border-zinc-800/60">
                        <td colSpan={4} className="px-4 pb-2.5 pt-0">
                          <p data-testid={`payment-notes-${p.code}`} className="text-[11px] text-amber-400/90 bg-amber-400/5 border border-amber-400/15 rounded px-2 py-1">
                            <span className="uppercase tracking-[0.1em] text-amber-500/70 mr-1.5">Admin:</span>{p.admin_notes}
                          </p>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
      </Dialog>
      <PaymentResultDialog summary={payResult} onClose={() => setPayResult(null)} />
    </>
  );
};
